/**
 * The room's status, and how it reads on screen.
 *
 * Pure functions only. The modal renders them and `Workspace` renders the
 * button; nothing here touches React or the network, so the two questions worth
 * getting right — how stale is this, and what does the button say — are testable
 * on their own.
 */
import type { RoomStatus, StatusSection } from '../api/types'

/** What each section is called where somebody reads it. */
export const HEADINGS: Record<StatusSection['section'], string> = {
  now: 'Now',
  blocked: 'Blocked',
  up_next: 'Next',
}

/** Reading order, which is not arrival order. */
export const SECTION_ORDER: StatusSection['section'][] = ['now', 'blocked', 'up_next']

/**
 * How long ago, in the coarsest unit that is still honest.
 *
 * The same rule as `domain.describe_age` on the Python side, because a status
 * that reads "4h ago" in a terminal and "today" in a browser is two answers to
 * one question.
 */
export function describeAge(when: string | null, now: Date = new Date()): string {
  if (!when) return 'never'
  const then = new Date(when).getTime()
  if (Number.isNaN(then)) return 'at some point'
  const seconds = (now.getTime() - then) / 1000
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`
  const days = Math.floor(seconds / 86_400)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

/** Whole days since a timestamp, for the staleness threshold. */
export function daysSince(when: string, now: Date = new Date()): number {
  const then = new Date(when).getTime()
  if (Number.isNaN(then)) return 0
  return Math.floor((now.getTime() - then) / 86_400_000)
}

/**
 * A status untouched for this long reads as abandoned rather than settled.
 *
 * Two weeks, because a room where nothing has changed for a fortnight is either
 * genuinely finished or quietly rotting, and the button cannot tell which. It
 * says how old and lets a person decide.
 */
export const STALE_AFTER_DAYS = 14

/**
 * How many things are blocked.
 *
 * Top-level `- ` items, and zero for a section written as prose — which the
 * button then reports as blocked without a number rather than guessing at one.
 */
export function blockedCount(body: string): number {
  return body.split('\n').filter((line) => line.trimStart().startsWith('- ')).length
}

/**
 * The same age as `describeAge`, in the space a rail row has.
 *
 * "6d" rather than "6 days ago": beside a repository name in a 200px column
 * there is room for a number and a unit and nothing else. Coarser than
 * `describeAge` on purpose — anything under a minute is not news, and the only
 * question this answers is whether a blocker is an hour old or a fortnight.
 */
export function shortAge(when: string, now: Date = new Date()): string {
  const then = new Date(when).getTime()
  if (Number.isNaN(then)) return ''
  const seconds = Math.max(0, (now.getTime() - then) / 1000)
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86_400)}d`
}

export interface ButtonState {
  /** Whether to draw the accent dot. Reserved for something waiting on a human. */
  urgent: boolean
  /** The trailing count, or null when the button is just "Status". */
  note: string | null
}

/**
 * What the button says at rest.
 *
 * A modal hides staleness, which is the one thing a persistent band did well, so
 * the button has to carry the signal or the blocker that has been waiting three
 * weeks is buried exactly as it was before this existed. First match wins.
 *
 * **Never colour.** Green and red are semantic here (additions, deletions,
 * online) and there is one accent, so age is a dimmed count and nothing more.
 * The dot is the accent and it means one thing: somebody is waiting on a human.
 */
export function buttonState(status: RoomStatus | null, now: Date = new Date()): ButtonState {
  if (!status || status.sections.length === 0) return { urgent: false, note: 'never set' }

  const blocked = status.sections.find((section) => section.section === 'blocked')
  if (blocked) {
    const count = blockedCount(blocked.body)
    return { urgent: true, note: count > 0 ? `${count} blocked` : 'blocked' }
  }

  if (status.updated_at && daysSince(status.updated_at, now) >= STALE_AFTER_DAYS) {
    return { urgent: false, note: `${daysSince(status.updated_at, now)}d old` }
  }
  return { urgent: false, note: null }
}

export interface StatusLine {
  kind: 'item' | 'text'
  body: string
}

/**
 * Split a section into renderable lines.
 *
 * The whole formatting vocabulary: a line starting `- ` is a list item, and
 * everything else is a paragraph. Inline `@mentions` and `` `code` `` come from
 * `parseBody`, which the stream already uses.
 *
 * Deliberately not markdown, and deliberately not a markdown dependency. The
 * sections are the headings; a status that needs a heading inside a section is
 * past its cap and should be shorter, not richer.
 */
export function statusLines(body: string): StatusLine[] {
  return body
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) =>
      line.startsWith('- ')
        ? { kind: 'item' as const, body: line.slice(2) }
        : { kind: 'text' as const, body: line },
    )
}

/** Sections in reading order, whatever order they arrived in. */
export function inReadingOrder(sections: StatusSection[]): StatusSection[] {
  return [...sections].sort(
    (left, right) =>
      SECTION_ORDER.indexOf(left.section) - SECTION_ORDER.indexOf(right.section),
  )
}
